'use client';

import { lazy, type ComponentType } from 'react';

import { MessagePartPrimitive, useAuiState } from '@assistant-ui/react';
import { once } from 'lodash';
import { create } from 'zustand';

import { partIsStreaming } from '@/components/assistant-ui/thread/aui-selectors';
import { loadTextExtraction } from '@/lib/tts/text-extraction-loader';

function PlainText() {
  return (
    <p className="whitespace-pre-wrap">
      <MessagePartPrimitive.Text />
    </p>
  );
}

let loaded: ComponentType | undefined;
let replyStarted = false;

/** Called when this page starts its first run; from then on no message waits for formatting. */
export function noteReplyStarted(): void {
  replyStarted = true;
}

// Re-renders the parts that showed plain text while formatting was loading.
const useFormattingLoaded = create<{ isLoaded: boolean }>()(() => ({ isLoaded: false }));

function selectIsLoaded(state: { isLoaded: boolean }) {
  return state.isLoaded;
}

// Settles to plain text on a failed chunk load: Suspense does not catch a rejection, so it would
// take the whole chat to the error boundary. Plain text until a reload.
async function loadMarkdownText(): Promise<void> {
  try {
    const [{ MarkdownText }] = await Promise.all([import('./markdown-text'), loadTextExtraction()]);
    loaded = MarkdownText;
  } catch {
    loaded = PlainText;
  }
  useFormattingLoaded.setState({ isLoaded: true });
}

export const preloadMarkdownText = once(loadMarkdownText);

const SuspendingMarkdownText = lazy(async function waitForMarkdownText() {
  await preloadMarkdownText();
  return { default: loaded ?? PlainText };
});

// Only a message that appears before this page has started a reply, as in a restored
// conversation, waits for formatting; see the message list's Suspense boundary in thread.tsx. A
// suspension later would hide the whole list, even under a reply that arrives all at once, so a
// later message shows as plain text until formatting loads. React.lazy suspends on its first render
// even once loaded, so it only renders while loading.
export function LazyMarkdownText() {
  const isLoaded = useFormattingLoaded(selectIsLoaded);
  const isStreaming = useAuiState(partIsStreaming);
  if (isLoaded && loaded) {
    const MarkdownText = loaded;
    return <MarkdownText />;
  }
  return isStreaming || replyStarted ? <PlainText /> : <SuspendingMarkdownText />;
}
